<template>
  <div
    class="add-qualification bg-background"
    :class="{ invisible: isCameraActive }"
  >
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      新しい資格を追加
    </Header>

    <div class="py-4 mx-3">
      <CustomValidationObserver v-slot="{ handleSubmit }">
        <AddQualification
          v-model="inputValues"
          :qualificationOptions="filteredQualificationOptions"
          v-model:isCameraActive="isCameraActive"
        />
        <AppButton
          block
          class="mt-4"
          :disabled="isSubmitting"
          @click="handleSubmit(onClickSubmit)"
        >
          追加する
        </AppButton>
      </CustomValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import Header from "@/components/organisms/common/Header.vue";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { AbTest3113 } from "@/app/services/AbTestService";
import { accountService } from "@/app/services/AccountService";
import AddQualification from "@/components/organisms/register/AddQualification.vue";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { authStore } from "@/store/auth";
import { UserQualificationInputValues } from "@/types/qualification/EditableUserQualificationItems";
import { UserQualification } from "@/types/qualification/UserQualification";
import { showErrorModal, ErrorType } from "@/utils/showErrorModal";
import { Qualification } from "@/types/Master";

@Component({
  components: {
    Header,
    AppButton,
    FeatherIcon,
    FormSelectGroup,
    AddQualification
  }
})
export default class MypageAddQualification extends Vue {
  inputValues: UserQualificationInputValues = {
    image: null,
    qualification: null
  };
  qualifications: UserQualification[] = [];
  isCameraActive = false;
  isSubmitting = false;

  async created() {
    try {
      this.$loading.show();
      await this.fetchQualifications();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchQualifications() {
    const { results } = await accountService.fetchQualifications();
    this.qualifications = results;
  }

  get filteredQualificationOptions(): Qualification[] {
    return authStore.product.qualifications.filter(r =>
      this.qualifications.every(q => q.qualification !== r.id)
    );
  }

  async onClickSubmit() {
    if (this.isSubmitting) return;

    if (!this.inputValues.image) {
      await this.$bvModal.msgBoxOk(
        "資格証を撮影、またはファイル選択してください。",
        {
          centered: true
        }
      );
      return;
    }
    if (!this.inputValues.qualification) {
      await this.$bvModal.msgBoxOk("資格種別を選択してください。", {
        centered: true
      });
      return;
    }

    this.isSubmitting = true;

    try {
      this.$loading.show();
      await accountService.createQualifications(
        this.inputValues.image,
        this.inputValues.qualification
      );
      await this.$bvModal.msgBoxOk("資格を追加しました。", {
        okVariant: "success",
        centered: true
      });
      AbTest3113.registerEvent(
        AbTest3113.eventTypes.completeQualificationRegistration
      );
      sendAdjustEvent("FINISH_APPLY_QUALIFICATION");
      sendFirebaseCustomEvent("finish_apply_qualification", {
        session_id: authStore.sessionId || "unknown"
      });
      await this.$router.push({ name: "MypageAccount" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.isSubmitting = false;
      this.$loading.hide();
    }
  }
}
</script>

<style lang="scss" scoped>
.add-qualification {
  min-height: inherit;
}
</style>
